import {
  DestroyRef,
  inject,
  Injectable,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, Subject } from 'rxjs';

import { WebSocketService } from '@core/websocket/websocket.service';
import { WsProtobufService } from '@core/websocket/ws-protobuf.service';
import { SessionService } from '@core/services/session.service';
import {
  ActiveDriverRequest,
  DriverReceiveWorkingShiftEvent,
  DriverStateEvent,
  SessionType,
} from '@core/websocket/websocket.models';
import { EventId, RequestPath, ResponseStatus } from '@core/websocket/websocket.constants';

export type RefillStep =
  | 'form'           // Форма запиту (початковий стан)
  | 'pending'        // Запит надіслано, очікуємо підтвердження
  | 'approved'       // Запит підтверджено логістом
  | 'cancel-pending' // Запит на скасування надіслано, очікуємо підтвердження
  | 'rejected';      // Запит скасовано оператором

@Injectable({ providedIn: 'root' })
export class RefillService implements OnDestroy {
  private readonly ws = inject(WebSocketService);
  private readonly proto = inject(WsProtobufService);
  private readonly sessionService = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── State signals ────────────────────────────────────────────────────────

  private readonly _step = signal<RefillStep>('form');
  readonly step = this._step.asReadonly();

  private readonly _activeRequestId = signal<string | null>(null);
  readonly activeRequestId = this._activeRequestId.asReadonly();

  /** ID запиту на скасування (type=CancelDriverRequest), що очікує підтвердження */
  private readonly _cancelRequestId = signal<string | null>(null);

  private readonly _odometerImageId = signal<string | null>(null);
  readonly odometerImageId = this._odometerImageId.asReadonly();

  private readonly _receiptImageId = signal<string | null>(null);
  readonly receiptImageId = this._receiptImageId.asReadonly();

  /** Емітує коли оператор підтвердив запит на скасування заправки */
  readonly cancelApprovedConfirmed$ = new Subject<void>();

  /**
   * Стріляє лише при відхиленні через реальний WS-івент (DriverRequestRejected).
   * Не стріляє при ініціалізації з lastDriverState/workingShiftData.
   */
  private readonly _rejectedViaWsEvent$ = new Subject<void>();
  readonly rejectedViaWsEvent$ = this._rejectedViaWsEvent$.asObservable();

  /** Стріляє коли оператор відхилив запит на скасування підтвердженого запиту */
  private readonly _cancelRejectedViaWsEvent$ = new Subject<void>();
  readonly cancelRejectedViaWsEvent$ = this._cancelRejectedViaWsEvent$.asObservable();

  /**
   * ID запитів, скасованих у цій сесії.
   * Потрібно щоб ігнорувати їх у init(), коли lastDriverState ще застарілий.
   */
  private readonly _cancelledIds = new Set<string>();

  /**
   * ID запитів, для яких дані заправки вже були успішно надіслані.
   * Потрібно щоб ігнорувати їх у init(), якщо сервер ще тримає їх в activeDriverRequests.
   */
  private readonly _storedIds = new Set<string>();

  // ─── Constructor: WebSocket event subscriptions ───────────────────────────

  constructor() {
    // DriverRequestConfirmed → 'approved' або підтвердження скасування
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestConfirmed),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestConfirmedEvent(e.payload);
        if (this._step() === 'pending' && event.id === this._activeRequestId()) {
          this._step.set('approved');
        } else if (this._step() === 'cancel-pending' && event.id === this._cancelRequestId()) {
          this.resetState();
          this.cancelApprovedConfirmed$.next();
        }
      });

    // DriverRequestRejected → 'rejected' або відхилення cancel
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestRejected),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestEvent(e.payload);
        if (this._step() === 'pending' && event.id === this._activeRequestId()) {
          this._step.set('rejected');
          this._rejectedViaWsEvent$.next();
        } else if (this._step() === 'cancel-pending' && event.id === this._cancelRequestId()) {
          this._cancelRequestId.set(null);
          this._step.set('approved');
          this._cancelRejectedViaWsEvent$.next();
        }
      });

    // DriverStateEvent → синхронізація активного запиту
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverStateEvent),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        this.handleDriverStateEvent(this.proto.decodeDriverStateEvent(e.payload));
      });
  }

  ngOnDestroy(): void {}

  // ─── Public API ───────────────────────────────────────────────────────────

  /** Ініціалізація при відкритті сторінки заправки */
  init(): void {
    if (this._step() !== 'form') return; // Вже ініціалізовано через події

    const liveState = this.sessionService.lastDriverState();
    if (liveState) {
      this.initFromDriverStateEvent(liveState);
      return;
    }

    const wshData = this.sessionService.workingShiftData();
    if (wshData) {
      this.initFromWorkingShiftData(wshData);
    }
  }

  /** Надіслати запит на заправку */
  async sendRequest(): Promise<void> {
    const wshData = this.sessionService.workingShiftData();
    if (!wshData) return;

    const body = this.proto.encodeCreateDriverRequest(
      wshData.workingShift.id,
      SessionType.VehicleRefill,
      '',
      new Uint8Array(0),
    );

    const response = await this.ws.sendRequest(RequestPath.CreateDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`CreateDriverRequest failed: ${response.code}`);
    }

    if (response.body?.length) {
      const parsed = this.proto.decodeCreateDriverRequestResponse(response.body);
      this._activeRequestId.set(parsed.id);
    }

    this._step.set('pending');
  }

  /** Надіслати запит на скасування підтвердженого запиту (approved стан) */
  async requestCancelApprovedRequest(): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    const body = this.proto.encodeCancelDriverRequest(requestId);
    const response = await this.ws.sendRequest(RequestPath.RequestCancelDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`RequestCancelApprovedRequest failed: ${response.code}`);
    }

    if (response.body?.length) {
      const cancelId = this.proto.decodeCancelRequestResponse(response.body);
      if (cancelId) {
        this._cancelRequestId.set(cancelId);
      }
    }

    this._step.set('cancel-pending');
  }

  /** Скасувати запит (pending стан) */
  async cancelRequest(): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    this._cancelledIds.add(requestId);
    const body = this.proto.encodeCancelDriverRequest(requestId);
    const response = await this.ws.sendRequest(RequestPath.CancelDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      this._cancelledIds.delete(requestId);
      throw new Error(`CancelDriverRequest failed: ${response.code}`);
    }
    this.resetState();
  }

  setOdometerImageId(id: string | null): void {
    this._odometerImageId.set(id);
  }

  setReceiptImageId(id: string | null): void {
    this._receiptImageId.set(id);
  }

  /** Скинути стан до початкового */
  resetState(): void {
    const id = this._activeRequestId();
    if (id) this._storedIds.add(id);
    this._step.set('form');
    this._activeRequestId.set(null);
    this._cancelRequestId.set(null);
    this._odometerImageId.set(null);
    this._receiptImageId.set(null);
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private initFromDriverStateEvent(event: DriverStateEvent): void {
    const refillRequest = event.requests.find(
      (r) => r.type === SessionType.VehicleRefill
        && !this._cancelledIds.has(r.id)
        && !this._storedIds.has(r.id),
    );
    if (!refillRequest) return;

    this._activeRequestId.set(refillRequest.id);
    const step = this.mapRequestStatus(refillRequest.status);
    this._step.set(step);

    if (step === 'cancel-pending') {
      this.syncCancelRequestId(event.requests, refillRequest.id);
    }
  }

  private initFromWorkingShiftData(data: DriverReceiveWorkingShiftEvent): void {
    const refillRequest = data.activeDriverRequests.find(
      (r) => r.type === SessionType.VehicleRefill
        && !this._cancelledIds.has(r.id)
        && !this._storedIds.has(r.id),
    );
    if (!refillRequest) return;

    this._activeRequestId.set(refillRequest.id);
    const step = this.mapRequestStatus(refillRequest.status);
    this._step.set(step);

    if (step === 'cancel-pending') {
      this.syncCancelRequestId(data.activeDriverRequests, refillRequest.id);
    }
  }

  private mapRequestStatus(status: string): RefillStep {
    if (status === 'confirmed') return 'approved';
    if (status === 'waiting_cancel') return 'cancel-pending';
    return 'pending';
  }

  private handleDriverStateEvent(event: DriverStateEvent): void {
    if (this._step() === 'form') {
      this.initFromDriverStateEvent(event);
      return;
    }

    if (this._step() === 'pending') {
      const refillRequest = event.requests.find((r) => r.type === SessionType.VehicleRefill);
      if (refillRequest) {
        this._activeRequestId.set(refillRequest.id);
      }
    }

    if (this._step() === 'cancel-pending' && !this._cancelRequestId()) {
      const activeId = this._activeRequestId();
      if (activeId) {
        this.syncCancelRequestId(event.requests, activeId);
      }
    }
  }

  /**
   * Знаходить запит скасування (type=CancelDriverRequest) у списку запитів,
   * декодує його payload і зберігає ID якщо він відповідає refillRequestId.
   */
  private syncCancelRequestId(requests: ActiveDriverRequest[], refillRequestId: string): void {
    const cancelRequest = requests.find(
      (r) => r.type === SessionType.CancelDriverRequest && r.payload?.length,
    );
    if (!cancelRequest?.payload) return;
    try {
      const payload = this.proto.decodeCancelDriverRequestPayload(cancelRequest.payload as Uint8Array);
      if (payload.requestId === refillRequestId) {
        this._cancelRequestId.set(cancelRequest.id);
      }
    } catch { /* ignore decode errors */ }
  }
}
